import { createSelector } from "reselect"
import { keys, find, filter as _filter, get, chain, isEmpty, mapValues, pickBy } from "lodash"
import { getCountries, getCurrencies, getClassificationFacetTrees } from "modules/securities-finder-facets/selectors"
import { getSelectedDate } from "./dates"
import { classificationsMap } from "helpers/classification"
import { ensureValidClassificationFilters } from "helpers/securities-search"
import { getCurrency as getPortfolioCurrency } from "modules/portfolio-root/selectors/portfolios"

export const getFilter = state => state.modules.securitiesFinder.filter
export const getEditingFilter = state => state.modules.securitiesFinder.editing
export const getCountryFacets = state => state.modules.securitiesFinder.country
export const getCurrencyFacets = state => state.modules.securitiesFinder.currency
export const getClassificationFacets = state => state.modules.securitiesFinder.classifications
const getCurrencyOverride = (_, props) => props.currency
const getClassifications = (state) => get(state, "global.data.classifications.data.classifications")
const getClassificationsValues = (state) => get(state, "global.data.classificationsValues.data")

export const getClassificationsTiers = createSelector(
  [getClassifications, getSelectedDate],
  (classifications, selectedDate) => classificationsMap(classifications, selectedDate),
)

export const getValidFilter = createSelector(
  [getFilter, getClassifications, getClassificationsValues],
  ensureValidClassificationFilters,
)

export const getFilterId = createSelector(
  [getFilter],
  filter => filter ? JSON.stringify(filter) : null,
)

export const getClassificationFilteredFacets = createSelector(
  [getClassificationFacets, getClassificationFacetTrees],
  (classificationFacets, availableClassifications) => mapValues(classificationFacets, (c, cId) =>
    pickBy(c, (f, fId) =>
      get(availableClassifications, [cId, f.tier, "facets", fId]),
    ),
  ),
)

export const getFacetedFilter = createSelector(
  [getValidFilter, getCountryFacets, getCurrencyFacets, getClassificationFilteredFacets, getCountries, getCurrencies],
  (filter, countryFacets, currencyFacets, classificationFacets, availableCountries, availableCurrencies) => {
    if (!filter) {
      return null
    }

    let query = { ...filter }

    let countries = keys(countryFacets)
    countries = _filter(countries, c => find(availableCountries, ac => ac.id === c))
    if (countries.length) {
      query.country = {
        in: countries,
      }
    }

    let currencies = keys(currencyFacets)
    currencies = _filter(currencies, c => find(availableCurrencies, ac => ac.id === c))
    if (currencies.length) {
      query.currency = {
        in: currencies,
      }
    }

    const classificationsFacetsFilter = chain(classificationFacets)
      .map((classification, id) => ({
        id,
        in: chain(classification)
          .map((facet, facetId) => ({ value: facetId, ...facet }))
          .filter(({ selected }) => selected)
          .map(({ value, tier }) => ({ value, tier }))
          .value(),
      }))
      .filter((cFilter) => !isEmpty(get(cFilter, "in")))
      .value()

    let existingClassifications = get(query, "classification", [])
    if (!Array.isArray(existingClassifications)) {
      existingClassifications = [existingClassifications]
    }
    query = {
      ...query,
      classification: [
        ...existingClassifications,
        ...classificationsFacetsFilter,
      ],
    }
    return query
  },
)

export const getFacetedFilterId = createSelector(
  [getFacetedFilter],
  filter => filter ? JSON.stringify(filter) : null,
)

export const getCurrency = createSelector(
  [getPortfolioCurrency, getCurrencyOverride],
  (portfolioCurrency, currencyOverride) => currencyOverride || portfolioCurrency,
)
